// Zustand store pro auth state — driver + JWT token v secure storage.

import { create } from "zustand";
import type { DriverBrief } from "@routera/shared";
import { api, saveToken, clearToken, loadToken } from "../lib/api";
import {
  resetSession, flushBeforeLogin, getLastDriverId, setLastDriverId,
} from "../lib/sessionReset";

interface AuthState {
  driver: DriverBrief | null;
  token: string | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;

  /** Pri startu app — pokusi se obnovit session z Preferences. */
  init: () => Promise<void>;

  /** Login: PIN -> JWT + driver, ulozi do Preferences. */
  login: (pin: string) => Promise<void>;

  /** Logout: smaze token + state. */
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  driver: null,
  token: null,
  loading: false,
  initialized: false,
  error: null,

  init: async () => {
    const stored = await loadToken();
    if (!stored) {
      set({ initialized: true });
      return;
    }
    // Overit token volanim /mobile/me
    try {
      const driver = await api.auth.whoami();
      set({ driver, token: stored, initialized: true });
    } catch {
      // Expired / invalid -> smaz a zustaň anonym
      await clearToken();
      set({ initialized: true });
    }
  },

  login: async (pin: string) => {
    set({ loading: true, error: null });
    try {
      // Frontu odešli JEŠTĚ pod starým tokenem — po jeho výměně by backend
      // události cizí směny odmítl a data by se zahodila při wipe níž.
      const previousDriverId = await getLastDriverId();
      if (previousDriverId) await flushBeforeLogin();

      const resp = await api.auth.login(pin);

      // Cache a stav předchozí session musí padnout PŘED nastavením nového
      // drivera — jinak by první render sáhl na `route/today` cizího řidiče.
      // Neodeslané fronty mažeme jen když se řidič opravdu změnil.
      await resetSession({
        wipePendingQueues: !!previousDriverId && previousDriverId !== resp.driver.id,
      });

      await saveToken(resp.token);
      await setLastDriverId(resp.driver.id);
      set({
        driver: resp.driver,
        token: resp.token,
        loading: false,
      });
    } catch (e: any) {
      set({ loading: false, error: e?.message ?? "Chyba prihlaseni" });
      throw e;
    }
  },

  logout: async () => {
    // Fronty NEmažeme — neodeslaná práce odcházejícího řidiče musí přežít;
    // zahodí se až kdyby se přihlásil někdo jiný.
    await clearToken();
    await resetSession({ wipePendingQueues: false });
    set({ driver: null, token: null, error: null });
  },
}));
